import type { ReactNode } from "react";

export type IconName =
  | "live" | "fleet" | "alerts" | "oee" | "safety" | "network"
  | "assets" | "devices" | "warehouses" | "mapping" | "sites" | "shifts" | "access"
  | "guide" | "home" | "check" | "chevron" | "menu";

/** Hand-rolled stroke icons (no icon dependency); all inherit `currentColor`. */
const PATHS: Record<IconName, ReactNode> = {
  live: (
    <>
      <circle cx="12" cy="12" r="2.2" fill="currentColor" stroke="none" />
      <path d="M7.7 7.7a6.1 6.1 0 0 0 0 8.6M16.3 7.7a6.1 6.1 0 0 1 0 8.6" />
      <path d="M5 5a10 10 0 0 0 0 14M19 5a10 10 0 0 1 0 14" opacity=".55" />
    </>
  ),
  fleet: (
    <>
      <path d="M4 15V8h6l2.2 4.5V15z" />
      <path d="M14.5 4.5V13m0 0h5" />
      <circle cx="6.5" cy="17.5" r="1.7" />
      <circle cx="12" cy="17.5" r="1.7" />
    </>
  ),
  alerts: (
    <>
      <path d="M12 4.2 21 19.2H3z" />
      <path d="M12 10v4.2M12 16.6v.4" />
    </>
  ),
  oee: (
    <>
      <path d="M4.5 16.5a7.5 7.5 0 1 1 15 0" />
      <path d="m12 16.5 3.6-5.4" />
      <circle cx="12" cy="16.5" r="1" fill="currentColor" stroke="none" />
    </>
  ),
  safety: <path d="M12 3.2 19 6v5.2c0 4.3-2.9 7.5-7 8.8-4.1-1.3-7-4.5-7-8.8V6z" />,
  network: (
    <>
      <circle cx="12" cy="5.5" r="2" />
      <circle cx="5" cy="18" r="2" />
      <circle cx="19" cy="18" r="2" />
      <path d="M11 7.3 6 16.2M13 7.3l5 8.9M7 18h10" />
    </>
  ),
  assets: (
    <>
      <path d="M12 3.2 20 7.7v8.6l-8 4.5-8-4.5V7.7z" />
      <path d="m4 7.7 8 4.5 8-4.5M12 12.2v8.6" />
    </>
  ),
  devices: (
    <>
      <rect x="7" y="7" width="10" height="10" rx="1.5" />
      <path d="M9.5 3.5V7M14.5 3.5V7M9.5 17v3.5M14.5 17v3.5M3.5 9.5H7M3.5 14.5H7M17 9.5h3.5M17 14.5h3.5" />
    </>
  ),
  warehouses: (
    <>
      <path d="M3.5 20V9.2L12 4.2l8.5 5V20" />
      <path d="M8.5 20v-6.5h7V20M2.5 20h19" />
    </>
  ),
  mapping: (
    <>
      <path d="M9 4.5 3.5 6.4v13.1L9 17.6l6 1.9 5.5-1.9V4.5L15 6.4z" />
      <path d="M9 4.5v13.1M15 6.4v13.1" />
    </>
  ),
  sites: (
    <>
      <circle cx="12" cy="12" r="8.3" />
      <path d="M3.7 12h16.6" />
      <path d="M12 3.7c3.1 2.5 3.1 14.1 0 16.6-3.1-2.5-3.1-14.1 0-16.6z" />
    </>
  ),
  shifts: (
    <>
      <circle cx="12" cy="12" r="8.3" />
      <path d="M12 7.4V12l3.2 2" />
    </>
  ),
  access: (
    <>
      <circle cx="8" cy="12" r="3.3" />
      <path d="M11.3 12H20M17 12v3M20 12v2.6" />
    </>
  ),
  guide: (
    <>
      <path d="M6 21V4" />
      <path d="M6 4.5h11.5l-2.2 3.2 2.2 3.3H6" />
    </>
  ),
  home: (
    <>
      <path d="M4 10.5 12 4l8 6.5V19a1 1 0 0 1-1 1h-4v-6H9v6H5a1 1 0 0 1-1-1z" />
    </>
  ),
  check: <path d="M5 12.5 10 17.5 19 6.5" />,
  chevron: <path d="M14.5 6.5 9 12l5.5 5.5" />,
  menu: <path d="M4 7h16M4 12h16M4 17h16" />,
};

export function NavIcon({ name, className }: { name: IconName; className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24" width="18" height="18"
      aria-hidden="true" focusable="false"
      fill="none" stroke="currentColor" strokeWidth="1.7"
      strokeLinecap="round" strokeLinejoin="round"
      className={className}
    >
      {PATHS[name]}
    </svg>
  );
}
